export class SqlOutbox {
    db;
    clock;
    constructor(db, clock) {
        this.db = db;
        this.clock = clock;
    }
    /**
     * Згортання перед вставкою — не оптимізація.
     *
     * Кожне натискання клавіші в редакторі зберігає запис; за годину
     * письма це тисячі дельт на один рядок, і всі, крім останньої,
     * заздалегідь програють LWW. Для тіла те саме з іншої причини:
     * у черзі лежить **повний** стан Yjs, тож остання поглинає всі
     * попередні за визначенням.
     */
    async put(table, rowId, hlc, payload) {
        await this.db.exec("DELETE FROM sync_outbox WHERE table_name = ? AND row_id = ?", [
            table,
            rowId,
        ]);
        await this.db.exec("INSERT INTO sync_outbox (table_name, row_id, hlc, payload, created_at) VALUES (?,?,?,?,?)", [table, rowId, hlc, payload, this.clock.now()]);
    }
    async take(limit) {
        const rows = await this.db.query("SELECT seq, table_name, row_id, hlc, payload FROM sync_outbox ORDER BY seq LIMIT ?", [
            limit,
        ]);
        return rows.map((r) => ({
            seq: r.seq,
            table: r.table_name,
            rowId: r.row_id,
            hlc: r.hlc,
            payload: r.payload,
        }));
    }
    /**
     * Знімає надіслане за `seq`, а не за рядком.
     *
     * Між `take` і `drop` лежить мережа, і за цей час користувач міг
     * знову правити той самий запис — тоді `put` уже поклав **нову**
     * дельту з новим `seq`. Видалення за `row_id` викинуло б і її,
     * і свіжа правка не поїхала б ніколи.
     */
    async drop(seqs) {
        if (seqs.length === 0)
            return;
        const holes = seqs.map(() => "?").join(",");
        await this.db.exec(`DELETE FROM sync_outbox WHERE seq IN (${holes})`, [...seqs]);
    }
    async has(table, rowId) {
        const rows = await this.db.query("SELECT COUNT(*) AS n FROM sync_outbox WHERE table_name = ? AND row_id = ?", [table, rowId]);
        return (rows[0]?.n ?? 0) > 0;
    }
    async count() {
        const rows = await this.db.query("SELECT COUNT(*) AS n FROM sync_outbox");
        return rows[0]?.n ?? 0;
    }
}
/**
 * Записи з чергою.
 *
 * Запечатування — **до** транзакції: воно єдине може кинути, і краще
 * хай кине, доки база не зрушила. Далі рядок і його дельта лягають
 * разом або не лягають зовсім; обрив між ними лишив би правку, яка
 * ніколи не поїде, і жодного сліду про це.
 */
export class OutboxEntryRepo {
    inner;
    o;
    constructor(inner, o) {
        this.inner = inner;
        this.o = o;
    }
    byId(id) {
        return this.inner.byId(id);
    }
    byDate(date) {
        return this.inner.byDate(date);
    }
    inRange(fromKey, toKey) {
        return this.inner.inRange(fromKey, toKey);
    }
    page(after, limit) {
        return this.inner.page(after, limit);
    }
    byKind(kind, limit) {
        return this.inner.byKind(kind, limit);
    }
    feed(before, limit) {
        return this.inner.feed(before, limit);
    }
    async save(entry) {
        if (!this.o.enabled())
            return this.inner.save(entry);
        const payload = await this.o.toPayload.entry(entry);
        await this.o.db.transaction(async () => {
            await this.inner.save(entry);
            await this.o.outbox.put("entry", entry.id, entry.hlc, payload);
        });
        this.o.onQueued?.();
    }
}
/**
 * Горизонти з чергою — точніше, **рядки плану** з чергою.
 *
 * Сам горизонт не синхронізується взагалі: у його рядку немає нічого,
 * що ввів користувач (`title` не пишеться ніде), а ідентифікатор тепер
 * виводиться з ключа ([ADR-0023]). Кожен пристрій матеріалізує горизонт
 * собі сам із `kind` і `key`, які несе дельта рядка.
 */
export class OutboxHorizonRepo {
    inner;
    o;
    constructor(inner, o) {
        this.inner = inner;
        this.o = o;
    }
    ensure(kind, key) {
        return this.inner.ensure(kind, key);
    }
    find(kind, key) {
        return this.inner.find(kind, key);
    }
    byId(id) {
        return this.inner.byId(id);
    }
    itemsOf(horizonId) {
        return this.inner.itemsOf(horizonId);
    }
    itemsInRange(kind, fromKey, toKey) {
        return this.inner.itemsInRange(kind, fromKey, toKey);
    }
    countsInRange(kind, fromKey, toKey) {
        return this.inner.countsInRange(kind, fromKey, toKey);
    }
    itemById(id) {
        return this.inner.itemById(id);
    }
    rolledFrom(id) {
        return this.inner.rolledFrom(id);
    }
    rollCounts(ids) {
        return this.inner.rollCounts(ids);
    }
    async saveItem(item) {
        if (!this.o.enabled())
            return this.inner.saveItem(item);
        /**
         * Прямим запитом, а не через `byId`: той відсіює видалені горизонти,
         * а рядок плану на видаленому горизонті існує (`ensure` його
         * воскрешає, але tombstone міг стояти в мить запису). Без `kind`
         * і `key` дельту не скласти, і правка тихо не поїхала б.
         */
        const rows = await this.o.db.query("SELECT kind, key FROM horizon WHERE id = ?", [item.horizonId]);
        const horizon = rows[0];
        if (!horizon)
            throw new Error(`горизонт ${item.horizonId} зник до запису рядка плану`);
        const payload = await this.o.toPayload.item(item, horizon.kind, horizon.key);
        await this.o.db.transaction(async () => {
            await this.inner.saveItem(item);
            await this.o.outbox.put("horizon_item", item.id, item.hlc, payload);
        });
        this.o.onQueued?.();
    }
}
//# sourceMappingURL=outbox.js.map